import React, { useCallback, useId } from "react";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import "./AccountDetails.scss";
import { format } from "date-fns";
import { useCanRemoveFinancialAccount } from "@hooks/permissions/canRemoveFinancialAccount";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { EMPTY_STATE } from "@common/constants";
import { getReconcileDisabledReason } from "@helpers/financialAccount";
import { getAnonymousAccountNumber } from "@common/helpers/bankAccount";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import {
    getConnectionLabel,
    isFinancialConnection,
} from "@common/helpers/financialConnection";
import { isStripeExpressConnection } from "@common/helpers/stripeConnection";
import {
    ACCOUNTS_POSSIBLY_PERSONAL,
    getLeadingAccountConnection,
    isCardAccount,
} from "@common/helpers/financialAccount";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import {
    getIntegrationConnectionInstitutionName,
    isIntegrationConnection,
} from "@common/helpers/integrationConnection";
import { isConnectionOwner } from "@hooks/useConnectionOwnership";
import { useUser } from "@hooks/useUser";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { useLockedTransactionsForFinancialAccount } from "@hooks/useLockedTransactionsForFinancialAccount";
import { ExtendedFinancialAccount } from "@lib/financialAccount";
import { Switch } from "@design-system/Switch/Switch";
import { Button } from "@design-system/Button/Button";
import {
    useAccountDisablingMutation,
    useAccountEnablingMutation,
} from "@mutations/financialAccount";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AlertIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { UpdateFinancialAccountEntity } from "../../../financialAccount/UpdateFinancialAccountEntity";
import { useEntity } from "../../../entity/useEntity";
import { FinancialAccountRoutingNumbers } from "../../../financialAccount/FinancialAccountRoutingNumbers";
import { RemoveFinancialAccount } from "../../../financialAccount/RemoveFinancialAccount/RemoveFinancialAccount";
import { SyncGustoConnection } from "../../Payroll/SyncGustoConnection";
import { MergeAccounts } from "../MergeAccounts/MergeAccounts";
import { useSettings } from "../../../settings/SettingsProvider";
import { AccountTypeChange } from "./AccountTypeChange";
import { MergedAccountsDetails } from "./MergedAccountsDetails";
import { AccountFinancialDocuments } from "./AccountFinancialDocuments";
import { AccountName } from "./AccountName";
import { FinancialAccountInfo } from "./FinancialAccountInfo";
import { GustoAccountDetails } from "./GustoAccountDetails";
import { AccountDetailsField } from "./AccountDetailsField";
import { AccountConnectionStatus } from "./AccountConnectionStatus";
import { AccountStatementsInfo } from "./AccountStatementsInfo";
import { AccountStartDate } from "./AccountStartDate";
import { AccountEndDate } from "./AccountEndDate";
import { AccountContinuousReconciliation } from "./AccountContinuousReconciliation";
import { StripeExpressDashboardButton } from "../../../stripe/StripeExpressDashboardButton";
import { StripeExpressBrandingSection } from "./StripeExpressBrandingSection";
import { StripeInvoiceJournalEntryModeField } from "./StripeInvoiceJournalEntryModeField";

interface AccountDetailsProps {
    account: ExtendedFinancialAccount;
    isOpened: boolean;
}

function getAccountNumber(account: ExtendedFinancialAccount) {
    if (account.accountNumberMask) {
        return getAnonymousAccountNumber(account.accountNumberMask);
    } else {
        return EMPTY_STATE;
    }
}

export const AccountDetails: React.FC<AccountDetailsProps> = ({
    account,
    isOpened,
}) => {
    const { toast } = useToaster();
    const entity = useEntity();
    const connection = getLeadingAccountConnection(account);
    const connectionUser =
        connection &&
        (isIntegrationConnection(connection)
            ? connection.createdByUser
            : connection.user);
    const connectionProvider =
        connection &&
        (isIntegrationConnection(connection)
            ? connection.provider
            : connection.connectionProvider);
    const user = useUser();
    const {
        mergingAccounts,
        continuousReconciliation,
        invoiceJournalEntryMode,
    } = useTypedFlags();
    const canRemoveAccount = useCanRemoveFinancialAccount(account);
    const { hasLockedTransactions } =
        useLockedTransactionsForFinancialAccount(account);
    const canMergeAccounts = mergingAccounts;
    const { closeWithOverlay } = useSettings();
    const connectionTitleId = useId();

    const reconcileDisabledReason = getReconcileDisabledReason(account);
    const canReconcile = !reconcileDisabledReason;
    const { hasAccess: hasImporterAccess } = useAllowFeature(
        PREMIUM_FEATURES.IMPORTER,
    );
    const { hasAccess: hasReconciliationAccess } = useAllowFeature(
        PREMIUM_FEATURES.RECONCILIATION,
    );

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    // Refused at the click rather than at the upload: the importer would
    // otherwise open, take a file and parse it before the server said no
    // (PRO-11392).
    const handleImportDocument = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        closeWithOverlay("importer", true);
    }, [closeWithOverlay, refuseIfViewOnly]);

    const handleReconcile = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        closeWithOverlay("reconcilieFinancialAccount", {
            financialAccountId: account.id,
        });
    }, [closeWithOverlay, refuseIfViewOnly, account.id]);

    const enableAccount = useAccountEnablingMutation(account);
    const disableAccount = useAccountDisablingMutation(account);
    const canBePersonal = ACCOUNTS_POSSIBLY_PERSONAL[account.sourceType];

    const handleChangeAccountEnabled = useCallback(
        (enabled: boolean) => {
            if (enabled) {
                enableAccount.mutate();
                return;
            }

            disableAccount.mutate();
        },
        [disableAccount, enableAccount],
    );

    const isMissingClassification = account.isBusiness === null;
    const isMissingEntity = account.entity === null;

    const onEntityUpdated = useCallback(() => {
        toast("Account moved to a different entity");
    }, [toast]);

    const accountNumber = getAccountNumber(account);
    const stripeIntegrationAccounts =
        account.integrationAccounts?.filter(
            (integrationAccount) =>
                integrationAccount.sourceType ===
                FinancialAccountSourceType.STRIPE,
        ) ?? [];
    const hasMergedStripeIntegrationAccounts =
        stripeIntegrationAccounts.length > 1;
    const stripeExpressConnectionId =
        account.sourceType === FinancialAccountSourceType.STRIPE &&
        connection &&
        isFinancialConnection(connection) &&
        isStripeExpressConnection(connection)
            ? connection.id
            : null;

    return (
        <div className="account-details">
            <section className="account-details__section">
                <header className="account-details__section__header">
                    <h4>Details</h4>
                </header>

                <main className="account-details__section__content">
                    <AccountDetailsField title="Account name">
                        <AccountName account={account} />
                    </AccountDetailsField>

                    <AccountDetailsField
                        title={
                            <>
                                Account type
                                {isMissingClassification && (
                                    <Tooltip content="Required info missing">
                                        <span>
                                            <AlertIcon className="icon-color-red-600 ml-2" />
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        }
                        className="account_details__type"
                    >
                        {canBePersonal ? (
                            <AccountTypeChange
                                className="account-details__field__control"
                                disabled={hasLockedTransactions}
                                account={account}
                                connection={connection ?? undefined}
                                onChanged={onEntityUpdated}
                            />
                        ) : (
                            "Business"
                        )}
                    </AccountDetailsField>

                    <AccountDetailsField
                        title={
                            <>
                                Entity
                                {isMissingEntity && (
                                    <Tooltip content="Required info missing">
                                        <span>
                                            <AlertIcon className="icon-color-red-600 ml-2" />
                                        </span>
                                    </Tooltip>
                                )}
                            </>
                        }
                        className="account_details__entity"
                    >
                        <UpdateFinancialAccountEntity
                            account={account}
                            connection={connection ?? undefined}
                            isBusiness={account.entity?.isBusiness ?? undefined}
                            selectedEntity={account.entity ?? undefined}
                            disabled={hasLockedTransactions}
                            onUpdated={onEntityUpdated}
                        />
                    </AccountDetailsField>
                    <AccountDetailsField
                        title={
                            <>
                                Start date
                                <span className="account-details__field__hint">
                                    Starts this account later than the
                                    entity&apos;s bookkeeping start date.
                                </span>
                            </>
                        }
                    >
                        <AccountStartDate
                            financialAccount={account}
                            entity={entity}
                        />
                    </AccountDetailsField>
                    <AccountDetailsField title="End date">
                        <AccountEndDate
                            financialAccount={account}
                            entity={entity}
                        />
                    </AccountDetailsField>
                    {continuousReconciliation && canReconcile && (
                        <AccountDetailsField title="Continuous reconciliation">
                            <AccountContinuousReconciliation
                                financialAccount={account}
                            />
                        </AccountDetailsField>
                    )}
                </main>
            </section>

            {account.sourceType !== FinancialAccountSourceType.FILE_IMPORT &&
                account.sourceType !== FinancialAccountSourceType.MANUAL && (
                    <section className="account-details__section">
                        <header className="account-details__section__header">
                            <h4>Info</h4>
                        </header>

                        <main className="account-details__section__content">
                            {connection && (
                                <AccountDetailsField title="Account">
                                    {isIntegrationConnection(connection)
                                        ? getIntegrationConnectionInstitutionName(
                                              connection,
                                          )
                                        : getConnectionLabel(connection)}
                                </AccountDetailsField>
                            )}

                            {account.routingNumber && (
                                <AccountDetailsField title="Routing number">
                                    <FinancialAccountRoutingNumbers
                                        account={account}
                                    />
                                </AccountDetailsField>
                            )}

                            {account.sourceType ===
                            FinancialAccountSourceType.PLAID ? (
                                <AccountDetailsField
                                    title={
                                        isCardAccount(account)
                                            ? "Card number"
                                            : "Account number"
                                    }
                                >
                                    {accountNumber}
                                </AccountDetailsField>
                            ) : (
                                <FinancialAccountInfo account={account} />
                            )}

                            {account.sourceType ===
                            FinancialAccountSourceType.GUSTO ? (
                                <GustoAccountDetails account={account} />
                            ) : null}
                        </main>
                    </section>
                )}

            {connection && (
                <section className="account-details__section">
                    <header className="account-details__section__header">
                        <h4>Connection</h4>
                    </header>

                    <main className="account-details__section__content">
                        <AccountDetailsField
                            title={
                                <span id={connectionTitleId}>
                                    Connected to {connectionProvider}
                                </span>
                            }
                        >
                            <div className="account-details__connection__content">
                                <AccountConnectionStatus
                                    connection={connection}
                                    account={account}
                                />

                                <Switch
                                    checked={
                                        !account.disabled && !connection?.error
                                    }
                                    loading={
                                        enableAccount.isPending ||
                                        disableAccount.isPending
                                    }
                                    onCheckedChange={handleChangeAccountEnabled}
                                    aria-labelledby={connectionTitleId}
                                />
                            </div>
                        </AccountDetailsField>

                        {connectionUser && (
                            <AccountDetailsField title="Integration created by">
                                {isConnectionOwner(connection, user.id)
                                    ? "You"
                                    : connectionUser.name}
                                {connection.createdAt && (
                                    <>
                                        {" "}
                                        on{" "}
                                        {format(
                                            connection.createdAt,
                                            fullDateFullYearFormat,
                                        )}
                                    </>
                                )}
                            </AccountDetailsField>
                        )}

                        {invoiceJournalEntryMode
                            ? stripeIntegrationAccounts.map(
                                  (stripeIntegrationAccount) => (
                                      <StripeInvoiceJournalEntryModeField
                                          key={stripeIntegrationAccount.id}
                                          workspaceId={entity.workspaceId}
                                          integrationAccountId={
                                              stripeIntegrationAccount.id
                                          }
                                          stripeAccountId={
                                              hasMergedStripeIntegrationAccounts
                                                  ? stripeIntegrationAccount.sourceAccountId
                                                  : null
                                          }
                                          mode={
                                              stripeIntegrationAccount.invoiceJournalEntryMode
                                          }
                                          isExpress={isStripeExpressConnection(
                                              stripeIntegrationAccount.connection,
                                          )}
                                      />
                                  ),
                              )
                            : null}

                        <AccountStatementsInfo
                            account={account}
                            connection={connection}
                        />
                    </main>
                </section>
            )}
            {stripeExpressConnectionId != null ? (
                <StripeExpressBrandingSection
                    connectionId={stripeExpressConnectionId}
                    isOpened={isOpened}
                />
            ) : null}
            {canMergeAccounts && (
                <MergedAccountsDetails financialAccount={account} />
            )}

            <AccountFinancialDocuments account={account} />

            <AccountDetailsField>
                <HStack justify="between" className="w-100">
                    <HStack gap={12}>
                        {stripeExpressConnectionId != null ? (
                            <StripeExpressDashboardButton
                                connectionId={stripeExpressConnectionId}
                            />
                        ) : (
                            <>
                                {account.sourceType ===
                                FinancialAccountSourceType.GUSTO ? (
                                    <SyncGustoConnection
                                        entity={entity}
                                        variant="primary"
                                        className="account-details__resync-account"
                                    >
                                        Sync account
                                    </SyncGustoConnection>
                                ) : null}
                                {canMergeAccounts && (
                                    <MergeAccounts
                                        financialAccount={account}
                                        entity={entity}
                                        buttonProps={{
                                            variant: "primary",
                                            size: "md",
                                        }}
                                        buttonClass="account-details__merge-account"
                                    />
                                )}
                                {hasImporterAccess && (
                                    <Button
                                        variant="primary"
                                        size="md"
                                        onClick={handleImportDocument}
                                    >
                                        Import document
                                    </Button>
                                )}
                                {hasReconciliationAccess && (
                                    <Tooltip content={reconcileDisabledReason}>
                                        <Button
                                            variant="primary"
                                            size="md"
                                            disabled={!canReconcile}
                                            onClick={handleReconcile}
                                        >
                                            Reconcile
                                        </Button>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </HStack>

                    {canRemoveAccount && (
                        <RemoveFinancialAccount
                            account={account}
                            variant="destructive"
                            className="account-details__remove-account danger"
                        >
                            Remove account
                        </RemoveFinancialAccount>
                    )}
                </HStack>
            </AccountDetailsField>
        </div>
    );
};
